"use client"

import { useState, useCallback } from "react"
import { API_ENDPOINTS } from "../utils/constants"
import { useAuth } from "./useAuth"
import type { CaseStudy, SectionFeedback } from "../types/caseStudy"

const useApiCalls = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { token, getUserId } = useAuth()

  const makeRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string> || {}),
      }

      if (token) {
        headers["Authorization"] = `Bearer ${token}`
      }

      const response = await fetch(url, {
        headers,
        ...options,
      })

      if (!response.ok) {
        const errorBody = await response.text()

        if (response.status === 401) {
          window.dispatchEvent(
            new CustomEvent("auth-error", {
              detail: { status: 401, message: "Unauthorized access" },
            }),
          )
        }

        throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`)
      }

      return await response.json()
    } catch (err) {
      console.error("API request failed:", err)
      throw err
    }
  }

  const makeRequestBlob = async (url: string, options: RequestInit = {}): Promise<Blob> => {
    try {
      const headers: Record<string, string> = {
        ...(options.headers as Record<string, string> || {}),
      }

      if (token) {
        headers["Authorization"] = `Bearer ${token}`
      }

      const response = await fetch(url, {
        headers,
        ...options,
      })

      if (!response.ok) {
        const errorBody = await response.text()

        if (response.status === 401) {
          window.dispatchEvent(
            new CustomEvent("auth-error", {
              detail: { status: 401, message: "Unauthorized access" },
            }),
          )
        }

        throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`)
      }

      return await response.blob()
    } catch (err) {
      console.error("API blob request failed:", err)
      throw err
    }
  }

  const generateCaseStudy = useCallback(
    async (data: Record<string, any>) => {
      setLoading(true)
      setError(null)

      try {
        const requestData = {
          user_id: getUserId(),
          ...data,
        }

        const result = await makeRequest(API_ENDPOINTS.GENERATE, {
          method: "POST",
          body: JSON.stringify(requestData),
        })

        return result
      } catch (err) {
        setError((err as Error).message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [getUserId],
  )

  const checkJobStatus = useCallback(async (jobId: string): Promise<any> => {
    try {
      const result = await makeRequest(`${API_ENDPOINTS.STATUS}/${jobId}/status`)
      return result
    } catch (err) {
      console.error("Failed to check job status:", err)
      throw err
    }
  }, [])

  const downloadCaseStudy = useCallback(
    async (jobId: string): Promise<boolean> => {
      setLoading(true)
      setError(null)

      try {
        const jobStatus = await checkJobStatus(jobId)

        if (jobStatus.status !== "completed") {
          throw new Error("Case study is not ready for download")
        }

        const fileKey: string | undefined = jobStatus.result?.file_key
        const markdown: string | undefined = jobStatus.result?.markdown
        const jobType: string | undefined = jobStatus.type || jobStatus.job_type
        const _existingDownloadUrl: string | undefined = jobStatus.result?.download_url

        if (!fileKey && markdown && jobType === "markdown_import") {
          const blob = new Blob([markdown], { type: "text/markdown" })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.style.display = "none"
          a.href = url
          a.download = `case-study-${jobId}.md`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
          return true
        }

        if (!fileKey) {
          throw new Error("File key not found in job result")
        }

        const pathSegments = fileKey.split("/").map((segment) => encodeURIComponent(segment))
        const encodedPath = pathSegments.join("/")
        const blob = await makeRequestBlob(`${API_ENDPOINTS.DOWNLOAD}/${encodedPath}`)

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = `case-study-${jobId}.md`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        return true
      } catch (err) {
        setError((err as Error).message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [checkJobStatus],
  )

  const listJobs = useCallback(async (): Promise<any> => {
    setLoading(true)
    setError(null)

    try {
      const result = await makeRequest(`${API_ENDPOINTS.LIST_JOBS}?user_id=${getUserId()}`)
      return result
    } catch (err) {
      setError((err as Error).message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [getUserId])

  const getJobFamily = useCallback(async (jobId: string): Promise<any> => {
    try {
      const result = await makeRequest(`${API_ENDPOINTS.JOB_FAMILY}/${jobId}/family`)
      return result
    } catch (err) {
      console.error("Failed to get job family:", err)
      throw err
    }
  }, [])

  const submitRevision = useCallback(
    async (caseStudyData: Record<string, any>, feedbackData: { section_feedback?: SectionFeedback[] }, parentJobId: string) => {
      setLoading(true)
      setError(null)

      try {
        const allSections = [
          "title",
          "subtitle",
          "at_a_glance",
          "key_metrics",
          "testimonial",
          "challenges",
          "objectives",
          "outcomes",
          "partnership_details",
        ]

        const sectionsWithFeedback = feedbackData.section_feedback?.map((sf) => sf.section_name) || []

        const finishedSections = allSections.filter((section) => !sectionsWithFeedback.includes(section))
        
        if (!finishedSections.includes("testimonial")) {
          finishedSections.push("testimonial")
        }

        const requestData = {
          case_study_data: {
            ...caseStudyData,
            ai_suggestions: caseStudyData.ai_suggestions || [],
          },
          feedback: feedbackData,
          finished_sections: finishedSections,
          parent_job_id: parentJobId,
          user_id: getUserId(),
        }

        if (import.meta.env.DEV) {
          console.log("🔍 Revision API Request:", {
            url: API_ENDPOINTS.REVISE,
            payload: {
              ...requestData,
              feedback: {
                ...requestData.feedback,
                section_feedback: requestData.feedback.section_feedback?.length || 0,
              },
            },
          })
        }

        const result = await makeRequest(API_ENDPOINTS.REVISE, {
          method: "POST",
          body: JSON.stringify(requestData),
        })

        return result
      } catch (err) {
        setError((err as Error).message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [getUserId],
  )

  const transformContentForApi = (sectionKey: string, content: any): string => {
    const simpleStringFields = ["title", "subtitle", "at_a_glance", "why_choose_riscpoint"]
    if (simpleStringFields.includes(sectionKey)) {
      return content || ""
    }
    
    const simpleArrayFields = ["challenges", "objectives"]
    if (simpleArrayFields.includes(sectionKey)) {
      if (Array.isArray(content)) {
        return content
          .filter((item: string) => item && item.trim() !== "")
          .map((item: string) => `- ${item.trim()}`)
          .join("\n")
      }
      return content || ""
    }
    
    if (sectionKey === "key_metrics") {
      if (Array.isArray(content)) {
        return JSON.stringify(content)
      }
      return content || "[]"
    }
    
    if (sectionKey === "outcomes") {
      if (Array.isArray(content)) {
        return JSON.stringify(content)
      }
      return content || "[]"
    }
    
    if (sectionKey === "testimonial") {
      if (typeof content === "object" && content !== null) {
        return JSON.stringify(content)
      }
      return content || "{}"
    }
    
    if (sectionKey === "partnership_details") {
      if (typeof content === "object" && content !== null) {
        return JSON.stringify(content)
      }
      return content || "{}"
    }
    
    if (sectionKey === "call_to_action") {
      if (typeof content === "object" && content !== null) {
        return JSON.stringify(content)
      }
      return content || "{}"
    }
    
    return content || ""
  }

  const mergeSections = useCallback(
    async (jobId: string, sectionSelections: Record<string, string>, customContent: Record<string, any> = {}) => {
      setLoading(true)
      setError(null)

      try {
        const transformedCustomContent: Record<string, string> = {}
        Object.entries(customContent).forEach(([key, value]) => {
          transformedCustomContent[key] = transformContentForApi(key, value)
        })

        const currentJobStatus = await checkJobStatus(jobId)
        const currentCaseStudy: any = currentJobStatus?.result?.case_study || {}

        const requestData = {
          job_id: jobId,
          ...sectionSelections,
          custom_content: {
            ...transformedCustomContent,
            ...(currentCaseStudy.ai_suggestions && {
              ai_suggestions: Array.isArray(currentCaseStudy.ai_suggestions)
                ? currentCaseStudy.ai_suggestions.join("\n")
                : String(currentCaseStudy.ai_suggestions),
            }),
          },
          user_id: getUserId(),
        }

        console.log("🔍 Merge API Request:", {
          url: API_ENDPOINTS.MERGE_SECTIONS,
          payload: requestData,
          headers: { Authorization: `Bearer ${token ? "PRESENT" : "MISSING"}` },
        })

        const result = await makeRequest(API_ENDPOINTS.MERGE_SECTIONS, {
          method: "POST",
          body: JSON.stringify(requestData),
        })

        return result
      } catch (err) {
        setError((err as Error).message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [getUserId, token],
  )

  const retryOperation = useCallback(async (operation: (...args: any[]) => Promise<any>, ...args: any[]) => {
    setError(null)
    try {
      return await operation(...args)
    } catch (err) {
      setError((err as Error).message)
      throw err
    }
  }, [])

  return {
    loading,
    error,
    generateCaseStudy,
    submitGeneration: generateCaseStudy,
    checkJobStatus,
    downloadCaseStudy,
    listJobs,
    getJobFamily,
    submitRevision,
    mergeSections,
    retryOperation,
    clearError: () => setError(null),
  }
}

export default useApiCalls
